import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Plus, Loader2, FileText, Edit2, Trash2, Save, Package, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { jobTemplatesAPI } from '../lib/v2Api';
import { itemsAPI } from '../lib/api';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { MobileFAB } from '../components/MobileFAB';

const EMPTY_FORM = { name: '', description: '', items: [] };

export default function JobTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [items, setItems] = useState([]);
  const [itemSearch, setItemSearch] = useState('');

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await jobTemplatesAPI.list();
      setTemplates(res.data || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const res = await itemsAPI.list({ page_size: 500, is_active: true });
      setItems(res.data.items || []);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { fetchTemplates(); fetchItems(); }, [fetchTemplates, fetchItems]);

  const openCreate = () => {
    setEditingTemplate(null);
    setForm({ ...EMPTY_FORM });
    setDrawerOpen(true);
  };

  const openEdit = (template) => {
    setEditingTemplate(template);
    setForm({
      name: template.name,
      description: template.description || '',
      items: template.items.map(i => ({
        item_id: i.item_id,
        item_name: i.item_name,
        item_sku: i.item_sku,
        quantity: i.quantity,
        notes: i.notes || '',
      })),
    });
    setDrawerOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Template name is required'); return; }
    if (form.items.length === 0) { toast.error('Add at least one part'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        items: form.items.map(i => ({
          item_id: i.item_id,
          quantity: i.quantity,
          notes: i.notes || null,
        })),
      };
      if (editingTemplate) {
        await jobTemplatesAPI.update(editingTemplate.id, payload);
        toast.success('Template updated');
      } else {
        await jobTemplatesAPI.create(payload);
        toast.success('Template created');
      }
      setDrawerOpen(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await jobTemplatesAPI.remove(id);
      toast.success('Template deleted');
      setDeleteConfirm(null);
      fetchTemplates();
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const addItemToForm = (item) => {
    if (form.items.some(i => i.item_id === item.id)) {
      toast.error('Item already in template');
      return;
    }
    setForm(prev => ({
      ...prev,
      items: [...prev.items, {
        item_id: item.id,
        item_name: item.name,
        item_sku: item.sku,
        quantity: 1,
        notes: '',
      }],
    }));
    setItemSearch('');
  };

  const removeItemFromForm = (itemId) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.filter(i => i.item_id !== itemId),
    }));
  };

  const updateItemQty = (itemId, qty) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.map(i =>
        i.item_id === itemId ? { ...i, quantity: Math.max(1, parseInt(qty) || 1) } : i
      ),
    }));
  };

  const filteredItems = itemSearch.trim()
    ? items.filter(i =>
        (i.name || '').toLowerCase().includes(itemSearch.toLowerCase()) ||
        (i.sku || '').toLowerCase().includes(itemSearch.toLowerCase())
      ).slice(0, 8)
    : [];

  return (
    <Layout>
      <div className="space-y-6" data-testid="job-templates-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="job-templates-title">Job Templates</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Reusable parts lists for common job types
            </p>
          </div>
          <TooltipProvider><Tooltip><TooltipTrigger asChild>
            <Button onClick={openCreate} data-testid="create-template-btn" className="hidden md:inline-flex">
              <Plus className="w-4 h-4 mr-2" /> New Template
            </Button>
          </TooltipTrigger><TooltipContent>Create a reusable parts list template for jobs.</TooltipContent></Tooltip></TooltipProvider>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : templates.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <FileText className="w-12 h-12 text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-semibold mb-1">No templates yet</h3>
              <p className="text-sm text-muted-foreground mb-4">Create a template to speed up job kit creation.</p>
              <Button onClick={openCreate}>
                <Plus className="w-4 h-4 mr-2" /> New Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {templates.map(t => (
              <Card key={t.id} data-testid={`template-card-${t.id}`}>
                <CardContent className="flex items-center gap-4 py-4 px-5">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{t.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {t.item_count} part{t.item_count !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    {t.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{t.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <TooltipProvider><Tooltip><TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger><TooltipContent>Edit this template's name and parts list.</TooltipContent></Tooltip></TooltipProvider>
                    {deleteConfirm === t.id ? (
                      <div className="flex items-center gap-1">
                        <Button variant="destructive" size="sm" onClick={() => handleDelete(t.id)} data-testid={`confirm-delete-template-${t.id}`}>
                          Delete
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <TooltipProvider><Tooltip><TooltipTrigger asChild>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(t.id)} data-testid={`delete-template-${t.id}`}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </TooltipTrigger><TooltipContent>Delete this template permanently.</TooltipContent></Tooltip></TooltipProvider>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Template Drawer */}
        <MobileFAB onClick={openCreate} data-testid="mobile-fab-template" />
        <AppDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          title={editingTemplate ? 'Edit Template' : 'New Template'}
          description={editingTemplate ? 'Update the template and its parts list.' : 'Define a reusable parts list for a type of job.'}
          className="sm:max-w-xl"
          data-testid="template-drawer"
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setDrawerOpen(false)}>Cancel</Button>
              <Button type="submit" form="template-form" disabled={saving} data-testid="save-template-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingTemplate ? 'Update' : 'Create'} Template
              </Button>
            </>
          }
        >
          <form id="template-form" onSubmit={handleSave} className="space-y-5">
            <div>
              <Label htmlFor="template_name">Template Name *</Label>
              <Input
                id="template_name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Water Heater Replacement"
                data-testid="input-template-name"
              />
            </div>
            <div>
              <Label htmlFor="template_desc">Description</Label>
              <Textarea
                id="template_desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What type of job is this template for?"
                rows={2}
                data-testid="input-template-description"
              />
            </div>

            {/* Parts List */}
            <div className="space-y-3">
              <Label>Parts List ({form.items.length})</Label>

              {/* Item Search */}
              <div className="relative">
                <Input
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  placeholder="Search parts to add..."
                  data-testid="item-search-input"
                />
                {filteredItems.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-auto" data-testid="item-search-results">
                    {filteredItems.map(item => (
                      <button
                        key={item.id}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex items-center justify-between"
                        onClick={() => addItemToForm(item)}
                        data-testid={`add-item-${item.id}`}
                      >
                        <span className="truncate">{item.name}</span>
                        {item.sku && <span className="text-xs text-muted-foreground ml-2">{item.sku}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected items */}
              {form.items.length === 0 ? (
                <div className="text-center py-6 border border-dashed rounded-lg">
                  <Package className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-sm text-muted-foreground">Search and add parts above</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table data-testid="template-items-table">
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Item</TableHead>
                        <TableHead className="w-[80px] text-right">Qty</TableHead>
                        <TableHead className="w-[40px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {form.items.map(item => (
                        <TableRow key={item.item_id} data-testid={`template-item-row-${item.item_id}`}>
                          <TableCell>
                            <p className="font-medium text-sm">{item.item_name}</p>
                            {item.item_sku && <p className="text-[10px] text-muted-foreground">{item.item_sku}</p>}
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItemQty(item.item_id, e.target.value)}
                              className="h-7 w-[65px] text-xs font-mono text-right ml-auto"
                              data-testid={`template-item-qty-${item.item_id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItemFromForm(item.item_id)}
                              className="h-7 w-7 p-0"
                              data-testid={`remove-template-item-${item.item_id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </form>
        </AppDrawer>
      </div>
    </Layout>
  );
}
